import { parseCurrency, toCents } from 'components/Widget/shared/currency';
import { parsePercent } from 'components/Widget/shared/percent';
import { getContributionFrequency, getSavingsPeriod, MONTHS_PER_YEAR } from './constants';
import type { ApySavingsInput, ApySavingsResult, ApySavingsSnapshot } from './types';

const DAYS_PER_YEAR = 365;

const dollarsFromCents = (cents: number): number => cents / 100;

const createSnapshot = (
  elapsedMonths: number,
  initialDepositInCents: number,
  totalContributionsInCents: number,
  unroundedBalanceInCents: number,
): ApySavingsSnapshot => {
  const totalSavingsInCents = toCents(dollarsFromCents(unroundedBalanceInCents));
  // Interest is the cent remainder so independently rounded legend rows cannot drift from the header total.
  const totalInterestInCents = totalSavingsInCents - initialDepositInCents - totalContributionsInCents;
  const initialDeposit = dollarsFromCents(initialDepositInCents);
  const totalContributions = dollarsFromCents(totalContributionsInCents);
  const totalInterest = dollarsFromCents(totalInterestInCents);

  // Deriving the total from cent-denominated legend rows keeps their displayed sum exact.
  const totalSavings = initialDeposit + totalContributions + totalInterest;

  return { elapsedMonths, initialDeposit, totalContributions, totalInterest, totalSavings };
};

export const calculateApySavings = (input: ApySavingsInput): ApySavingsResult => {
  const initialDepositInCents = toCents(parseCurrency(input.startingBalance));
  const recurringContributionInCents = toCents(parseCurrency(input.recurringContribution));
  const apy = parsePercent(input.apy) / 100;
  const savingsPeriod = getSavingsPeriod(input.savingsPeriod);
  const contributionFrequency = getContributionFrequency(input.contributionFrequency);

  // Chime uses a nominal daily-compounded rate; compounding APY directly overstates returns.
  const dailyPeriodicRate = (1 + apy) ** (1 / DAYS_PER_YEAR) - 1;
  const nominalAnnualRate = DAYS_PER_YEAR * dailyPeriodicRate;
  const contributionPeriodInDays = DAYS_PER_YEAR / contributionFrequency.periodsPerYear;

  let unroundedBalanceInCents = initialDepositInCents;
  let elapsedDays = 0;
  let contributionCount = 0;
  let totalContributionsInCents = 0;

  const advanceBalance = (targetDay: number) => {
    unroundedBalanceInCents *= (1 + nominalAnnualRate / DAYS_PER_YEAR) ** (targetDay - elapsedDays);
    elapsedDays = targetDay;
  };

  const buckets = savingsPeriod.bucketMonths.map(elapsedMonths => {
    // Note: Fractional 365/12-day months are used here.
    const bucketEndDay = (elapsedMonths * DAYS_PER_YEAR) / MONTHS_PER_YEAR;
    while (true) {
      // Contributions post at the END because source materials omit timing; this conservative choice needs Legal sign-off to change.
      const nextContributionDay = (contributionCount + 1) * contributionPeriodInDays;

      if (nextContributionDay > bucketEndDay) break;

      advanceBalance(nextContributionDay);
      unroundedBalanceInCents += recurringContributionInCents;
      totalContributionsInCents += recurringContributionInCents;
      contributionCount += 1;
    }

    advanceBalance(bucketEndDay);

    return createSnapshot(elapsedMonths, initialDepositInCents, totalContributionsInCents, unroundedBalanceInCents);
  });
  const finalBucket = buckets[buckets.length - 1];

  if (!finalBucket) throw new Error(`Savings period has no buckets: ${input.savingsPeriod}`);

  return {
    buckets,
    initialDeposit: finalBucket.initialDeposit,
    totalContributions: finalBucket.totalContributions,
    totalInterest: finalBucket.totalInterest,
    totalSavings: finalBucket.totalSavings,
  };
};
